import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import ChartWorkspaceCrosshairContext from "./ChartWorkspaceCrosshairContext";
import ChartWorkspaceDrawingContext from "./ChartWorkspaceDrawingContext";
import ChartWorkspacePanel from "./ChartWorkspacePanel";
import DrawToolsPanel from "./DrawToolsPanel";
import IndicatorLibraryPanel from "./IndicatorLibraryPanel";
import {
  CHART_LAYOUTS,
  LINK_KEYS,
  PANEL_IDS,
  chartWorkspaceReducer,
  createInitialChartWorkspaceState,
  getVisiblePanes,
} from "./chartWorkspaceModel";
import { drawingReducer } from "./drawingModel";
import "./ProfessionalChartWorkspace.css";
import { traceChartNavigation } from "./chartNavigationTrace";

const LAYOUT_OPTIONS = [
  { id: CHART_LAYOUTS.SINGLE, label: "1" },
  { id: CHART_LAYOUTS.TWO_COLUMNS, label: "2 Col" },
  { id: CHART_LAYOUTS.TWO_ROWS, label: "2 Row" },
  { id: CHART_LAYOUTS.FOUR_GRID, label: "4 Grid" },
];

const LINK_OPTIONS = [
  { key: LINK_KEYS.SYMBOL, label: "Symbol", available: true },
  { key: LINK_KEYS.INTERVAL, label: "Interval", available: true },
  { key: LINK_KEYS.RANGE, label: "Range", available: true },
  { key: LINK_KEYS.NAVIGATION, label: "Navigation", available: true },
  { key: LINK_KEYS.CROSSHAIR, label: "Crosshair", available: true },
];

const RESET_NAVIGATION = Object.freeze({
  viewportEnd: null,
  futureSpaceBars: 0,
});

function ProfessionalChartWorkspace({
  symbol = "PLUG",
  renderPane,
  basicMode = false,
}) {
  const [state, dispatch] = useReducer(
    chartWorkspaceReducer,
    { symbol },
    createInitialChartWorkspaceState
  );
  const [favoriteIndicatorIds, setFavoriteIndicatorIds] = useState([]);
  const [selectedTools, setSelectedTools] = useState({});
  const [linkedCrosshairPoint, setLinkedCrosshairPoint] = useState(null);
  const previousExternalSymbolRef = useRef(symbol);

  const activePane = useMemo(
    () => state.panes.find((pane) => pane.id === state.activePaneId) || state.panes[0],
    [state.activePaneId, state.panes]
  );
  const visiblePanes = useMemo(() => {
    if (!basicMode) return getVisiblePanes(state);
    const primaryPane = state.panes[0];
    return primaryPane ? [{ ...primaryPane, symbol }] : [];
  }, [basicMode, state, symbol]);
  const crosshairLinked = state.links[LINK_KEYS.CROSSHAIR];
  const crosshairEnabled = basicMode || crosshairLinked;

  traceChartNavigation("ProfessionalChartWorkspace.render", {
    symbol,
    reason: "workspace render",
    activePaneId: state.activePaneId,
    panes: state.panes.map((pane) => ({
      id: pane.id,
      symbol: pane.symbol,
      navigation: pane.navigation,
    })),
  });

  useEffect(() => {
    if (!symbol || previousExternalSymbolRef.current === symbol) return;
    previousExternalSymbolRef.current = symbol;
    dispatch({
      type: "UPDATE_PANE",
      paneId: state.activePaneId,
      patch: { symbol, navigation: RESET_NAVIGATION },
    });
  }, [state.activePaneId, symbol]);

  useEffect(() => {
    if (!crosshairEnabled) setLinkedCrosshairPoint(null);
  }, [crosshairEnabled]);

  const updatePane = (paneId, patch) => {
    const previousPane = state.panes.find((pane) => pane.id === paneId);
    traceChartNavigation("ProfessionalChartWorkspace.updatePane", {
      symbol: previousPane?.symbol || symbol,
      paneId,
      reason: Object.prototype.hasOwnProperty.call(patch, "navigation")
        ? "controls.updatePane navigation patch"
        : "controls.updatePane non-navigation patch",
      previousNavigation: previousPane?.navigation || null,
      nextNavigation: patch.navigation || previousPane?.navigation || null,
      patchKeys: Object.keys(patch),
    }, { stack: Object.prototype.hasOwnProperty.call(patch, "navigation") });
    dispatch({ type: "UPDATE_PANE", paneId, patch });
  };

  const updateActivePane = (patch) => updatePane(activePane.id, patch);

  const applyDrawingAction = (action) => {
    updateActivePane({ drawings: drawingReducer(activePane.drawings, action) });
  };

  const toggleIndicator = (indicatorId) => {
    const active = activePane.indicators.includes(indicatorId);
    updateActivePane({
      indicators: active
        ? activePane.indicators.filter((id) => id !== indicatorId)
        : [...activePane.indicators, indicatorId],
    });
  };

  const toggleFavorite = (indicatorId) => {
    setFavoriteIndicatorIds((current) =>
      current.includes(indicatorId)
        ? current.filter((id) => id !== indicatorId)
        : [...current, indicatorId]
    );
  };

  const panelProps = (panelId) => ({
    panel: state.panels[panelId],
    onClose: (id) => dispatch({ type: "CLOSE_PANEL", panelId: id }),
    onMove: (id, position) =>
      dispatch({ type: "MOVE_PANEL", panelId: id, position }),
    onTogglePin: (id) => dispatch({ type: "TOGGLE_PANEL_PIN", panelId: id }),
    onFocus: (id) => dispatch({ type: "FOCUS_PANEL", panelId: id }),
  });

  return (
    <section className="professionalChartWorkspace" data-testid="professional-chart-workspace">
      {!basicMode && (
        <>
      <div className="professionalChartWorkspaceToolbar">
        <div className="professionalChartWorkspaceTools">
          <button
            type="button"
            aria-pressed={state.panels[PANEL_IDS.INDICATORS].isOpen}
            onClick={() => dispatch({ type: "TOGGLE_PANEL", panelId: PANEL_IDS.INDICATORS })}
          >
            Indicators
          </button>
          <button
            type="button"
            aria-pressed={state.panels[PANEL_IDS.DRAW].isOpen}
            onClick={() => dispatch({ type: "TOGGLE_PANEL", panelId: PANEL_IDS.DRAW })}
          >
            Draw
          </button>
        </div>

        <div className="professionalChartWorkspaceLayouts" aria-label="Chart layouts">
          {LAYOUT_OPTIONS.map((layout) => (
            <button
              key={layout.id}
              type="button"
              className={state.layout === layout.id && !state.focus.paneId ? "active" : ""}
              aria-pressed={state.layout === layout.id && !state.focus.paneId}
              onClick={() => dispatch({ type: "SET_LAYOUT", layout: layout.id })}
            >
              {layout.label}
            </button>
          ))}
        </div>
      </div>

      <div className="professionalChartWorkspaceLinks" aria-label="Chart linking">
        <span>Link panes</span>
        {LINK_OPTIONS.map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={state.links[option.key]}
            title={`Toggle linked ${option.label.toLowerCase()}`}
            onClick={() =>
              dispatch({
                type: "SET_LINK",
                key: option.key,
                enabled: !state.links[option.key],
              })
            }
          >
            {option.label}
          </button>
        ))}
      </div>

        </>
      )}

      <div className={`professionalChartWorkspaceGrid layout-${basicMode ? CHART_LAYOUTS.SINGLE : state.layout}`}>
        {visiblePanes.map((pane) => {
          const active = pane.id === state.activePaneId;
          const drawingContextValue = {
            paneId: pane.id,
            updateDrawing: (drawingId, patch) =>
              updatePane(pane.id, {
                drawings: drawingReducer(pane.drawings, {
                  type: "UPDATE_DRAWING",
                  drawingId,
                  patch,
                }),
              }),
          };
          const crosshairContextValue = {
            enabled: crosshairEnabled,
            point: linkedCrosshairPoint,
            setPoint: setLinkedCrosshairPoint,
          };

          return (
            <article
              key={pane.id}
              className={`professionalChartWorkspacePane${active ? " active" : ""}`}
              data-testid={`professional-chart-pane-${pane.id}`}
              onPointerDown={() => dispatch({ type: "SET_ACTIVE_PANE", paneId: pane.id })}
            >
              <div className="professionalChartWorkspacePaneBody">
                <ChartWorkspaceCrosshairContext.Provider value={crosshairContextValue}>
                  <ChartWorkspaceDrawingContext.Provider value={drawingContextValue}>
                    {renderPane
                      ? renderPane(pane, {
                          isActive: active,
                          selectedDrawingToolId: selectedTools[pane.id] || null,
                          setActive: () =>
                            dispatch({ type: "SET_ACTIVE_PANE", paneId: pane.id }),
                          updatePane: (patch) => updatePane(pane.id, patch),
                        })
                      : null}
                  </ChartWorkspaceDrawingContext.Provider>
                </ChartWorkspaceCrosshairContext.Provider>
              </div>
            </article>
          );
        })}
      </div>

      {!basicMode && (
        <>
        <ChartWorkspacePanel {...panelProps(PANEL_IDS.INDICATORS)}>
          <IndicatorLibraryPanel
            activeIndicatorIds={activePane.indicators}
            favoriteIndicatorIds={favoriteIndicatorIds}
            onToggleIndicator={toggleIndicator}
            onToggleFavorite={toggleFavorite}
          />
        </ChartWorkspacePanel>
  
        <ChartWorkspacePanel {...panelProps(PANEL_IDS.DRAW)}>
          <DrawToolsPanel
            selectedToolId={selectedTools[activePane.id] || null}
            drawings={activePane.drawings}
            onSelectTool={(toolId) =>
              setSelectedTools((current) => ({ ...current, [activePane.id]: toolId }))
            }
            onSelectDrawing={(drawingId) =>
              applyDrawingAction({ type: "SELECT_DRAWING", drawingId })
            }
            onToggleDrawingLock={(drawingId) =>
              applyDrawingAction({ type: "TOGGLE_DRAWING_LOCK", drawingId })
            }
            onToggleDrawingVisibility={(drawingId) =>
              applyDrawingAction({ type: "TOGGLE_DRAWING_VISIBILITY", drawingId })
            }
            onDeleteDrawing={(drawingId) =>
              applyDrawingAction({ type: "DELETE_DRAWING", drawingId })
            }
            onClearDrawings={() =>
              applyDrawingAction({ type: "CLEAR_PANE_DRAWINGS", paneId: activePane.id })
            }
          />
        </ChartWorkspacePanel>
        </>
      )}
    </section>
  );
}

export default ProfessionalChartWorkspace;
